import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { gamesAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Sparkles, Trash2, Play, DollarSign, Pencil } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';
import DashboardNavbar from '../components/DashboardNavbar';
import Seo from '../components/Seo';
import AdminCreditPanel from '../components/AdminCreditPanel';
import AdminUsersTable from '../components/AdminUsersTable';
import AdminAnalyticsPanel from '../components/AdminAnalyticsPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const Dashboard = () => {
  const { user, logout } = useAuth();
  // Frontend: treat this specific email as admin for UI purposes to avoid DB flag issues in production.
  const isAdmin = user?.email === 'trcd1@icloud.com';
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    loadGames();
    checkPaymentStatus();
  }, []);

  const checkPaymentStatus = async () => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) return;

    let attempts = 0;
    const maxAttempts = 5;
    const pollInterval = 2000; // 2 seconds

    const pollStatus = async () => {
      if (attempts >= maxAttempts) {
        toast({
          title: 'Payment Check',
          description: 'Payment verification taking longer than expected. Check your account later.',
          variant: 'warning',
        });
        return;
      }

      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${BACKEND_URL}/api/payments/status/${sessionId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.data.payment_status === 'paid') {
          toast({
            title: 'Payment Successful!',
            description: 'Your Pro subscription is now active. Enjoy more credits and features!',
          });
          navigate('/dashboard', { replace: true });
          return;
        }

        if (response.data.status === 'cancelled') {
          toast({
            title: 'Payment Cancelled',
            description: 'Your payment was cancelled or expired.',
            variant: 'destructive',
          });
          navigate('/dashboard', { replace: true });
          return;
        }

        attempts += 1;
        setTimeout(pollStatus, pollInterval);
      } catch (error) {
        console.error('Error checking payment status:', error);
        toast({
          title: 'Error',
          description: 'Failed to verify payment status',
          variant: 'destructive',
        });
        navigate('/dashboard', { replace: true });
      }
    };

    toast({
      title: 'Processing Payment',
      description: 'Please wait while we verify your payment...',
    });

    pollStatus();
  };

  const loadGames = async () => {
    try {
      const data = await gamesAPI.getUserGames();
      setGames(data.games);
    } catch (error) {
      console.error('Error loading games:', error);
      toast({
        title: 'Error',
        description: 'Failed to load games',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteGame = async (gameId) => {
    if (!window.confirm('Are you sure you want to delete this game?')) return;

    try {
      await gamesAPI.deleteGame(gameId);
      setGames((prev) => prev.filter((g) => g.game_id !== gameId));
      toast({
        title: 'Success',
        description: 'Game deleted successfully',
      });
    } catch (error) {
      console.error('Error deleting game:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete game',
        variant: 'destructive',
      });
    }
  };

  const handleClaimEarnings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${BACKEND_URL}/api/payouts/claim`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      toast({
        title: 'Earnings Claimed',
        description: `Payout of $${response.data.amount.toFixed(4)} initiated.`,
      });
    } catch (error) {
      console.error('Error claiming earnings:', error);
      toast({
        title: 'Claim Failed',
        description:
          error.response?.data?.detail || 'Unable to claim earnings right now.',
        variant: 'destructive',
      });
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const totalEarnings = games.reduce(
    (sum, game) => sum + (game.stats?.revenue || 0),
    0
  );

  const totalPlays = games.reduce(
    (sum, game) => sum + (game.stats?.plays || 0),
    0
  );

  return (
    <div className="min-h-screen bg-black">
      <Seo
        title="My Games Dashboard – GameOS"
        description="Manage your AI-generated games: play, edit, share, monetize and track earnings from your GameOS dashboard."
        path="/dashboard"
      />
      <DashboardNavbar onLogout={handleLogout} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Earnings Summary */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <div className="bg-gradient-to-br from-green-900/20 to-emerald-900/20 rounded-2xl p-6 border border-green-500/30">
            <div className="text-gray-400 mb-2">Total Earnings</div>
            <div className="text-3xl font-bold text-green-400 mb-4">
              ${totalEarnings.toFixed(2)}
            </div>
            <Button
              onClick={handleClaimEarnings}
              className="w-full bg-green-600 hover:bg-green-700 text-white"
            >
              <DollarSign size={16} className="mr-2" />
              Claim Earnings
            </Button>
          </div>

          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 border border-white/10">
            <div className="text-gray-400 mb-2">Total Games</div>
            <div className="text-3xl font-bold text-white">{games.length}</div>
            <p className="text-sm text-gray-500 mt-2">Games created</p>
          </div>

          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 border border-white/10">
            <div className="text-gray-400 mb-2">Total Plays</div>
            <div className="text-3xl font-bold text-white">{totalPlays}</div>
            <p className="text-sm text-gray-500 mt-2">Across all games</p>
          </div>
        </div>

        {/* Credits & subscription */}
        <div className="flex flex-wrap items-center gap-4 mb-6 text-sm text-gray-300">
          <div className="bg-gradient-to-r from-purple-900/40 to-fuchsia-900/40 rounded-full px-4 py-1 border border-purple-500/40">
            Credits left: <span className="font-semibold text-purple-200">{user?.credits ?? '∞'}</span>
          </div>
          <div className="bg-gradient-to-r from-emerald-900/40 to-teal-900/40 rounded-full px-4 py-1 border border-emerald-500/40">
            Plan: <span className="font-semibold text-emerald-200">{user?.subscription === 'pro' && user?.subscription_status === 'active' ? 'Pro' : 'Free'}</span>
          </div>
        </div>

        {isAdmin && (
          <div className="mb-8 space-y-4">
            <div className="bg-gradient-to-br from-yellow-900/60 to-amber-900/40 border border-yellow-500/60 rounded-2xl p-6">
              <h3 className="text-yellow-200 font-semibold mb-2 text-sm">Admin: Adjust User Credits</h3>
              <p className="text-xs text-yellow-100/80 mb-4">
                Add or remove credits for any user by email. Positive values add credits, negative values remove credits.
              </p>
              <AdminCreditPanel />

              <div className="mt-6">
                <h4 className="text-yellow-100 font-semibold mb-2 text-xs">Platform Overview</h4>
                <AdminUsersTable />
              </div>
            </div>

            <AdminAnalyticsPanel />
          </div>
        )}

        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-white mb-2">My Games</h1>
            <p className="text-gray-400">Create, manage, and play your AI-generated games</p>
          </div>
          <Button
            onClick={() => navigate('/create')}
            size="lg"
            className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
          >
            <Sparkles className="mr-2" size={20} />
            Create New Game
          </Button>
        </div>

        {/* Games Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="text-gray-400">Loading your games...</div>
          </div>
        ) : games.length === 0 ? (
          <div className="text-center py-12">
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-12 border border-white/10">
              <Sparkles className="w-16 h-16 text-purple-500 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-white mb-2">No games yet</h3>
              <p className="text-gray-400 mb-6">Start creating your first game with AI!</p>
              <Button
                onClick={() => navigate('/create')}
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
              >
                <Sparkles className="mr-2" size={16} />
                Create Your First Game
              </Button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {games.map((game) => (
              <div
                key={game.game_id}
                className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl overflow-hidden border border-white/10 hover:border-purple-500/50 transition-all duration-300"
              >
                {/* Game Thumbnail */}
                <div className="aspect-video bg-black/50 flex items-center justify-center">
                  {game.thumbnail ? (
                    <img
                      src={game.thumbnail}
                      alt={game.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="text-6xl">🎮</div>
                  )}
                </div>

                {/* Game Info */}
                <div className="p-5">
                  <h3 className="text-xl font-bold text-white mb-2">{game.title}</h3>
                  <p className="text-gray-400 text-sm mb-4 line-clamp-2">
                    {game.description}
                  </p>

                  {/* Tags */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {game.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="text-xs px-2 py-1 bg-purple-500/20 text-purple-300 rounded-full border border-purple-500/30"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>

                  {/* Stats */}
                  <div className="flex items-center justify-between text-sm text-gray-400 mb-4">
                    <div>Plays: {game.stats?.plays || 0}</div>
                    <div>Revenue: ${game.stats?.revenue?.toFixed(2) || '0.00'}</div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <Button
                      onClick={() => navigate(`/game/${game.game_id}`)}
                      className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
                    >
                      <Play size={16} className="mr-2" />
                      View
                    </Button>
                    <Button
                      onClick={() => navigate(`/create?gameId=${game.game_id}`)}
                      variant="outline"
                      className="border-blue-500 text-blue-400 hover:bg-blue-500/10 flex-1"
                    >
                      <Pencil size={16} className="mr-1" />
                      Edit with AI
                    </Button>
                    <Button
                      onClick={() => handleDeleteGame(game.game_id)}
                      variant="outline"
                      className="border-red-500 text-red-400 hover:bg-red-500/10"
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
